import { action, toJS } from 'mobx';
import {
  FieldType,
  MobxFieldControl
} from '@app/src/common/components/form/MobxFieldControl';
import { DateTimeService } from '@app/src/services/DateTimeService';
import { BaseForm, IBaseFormError } from './BaseForm';

export {
  isRequired,
  skipIf,
  isEmail,
  isSameAs,
  isMoreThan,
  isLessThan,
  lengthIsNotMoreThan,
  valueIsNotLessThan,
  valueIsNotMoreThan,
  isJSON,
  isPassword,
  skipCheckIf,
  lengthIsNotMoreThanIf
} from '@app/src/common/components/form/Validators';

export interface IMobxFormFactory {
  [key: string]:
    | null
    | string
    | number
    | boolean
    | undefined
    | File
    | { [key: string]: boolean }
    | string[]
    | { [key: string]: any }[];
}

export type IMobxFormError = IBaseFormError;

interface ApplySettings {
  disableParseDateToTimezone?: boolean;
}

type Apply = (changes: IMobxFormFactory, settings?: ApplySettings) => void;

export interface IMobxForm {
  isValid: boolean;
  isTouched: boolean;
  isEmpty: boolean;
  errors: IMobxFormError;
  useTimeZone: boolean;
  _isTouched: boolean;

  toggleTouched(touched: boolean): void;

  clear(): void;

  reset(): void;

  resetIsTouched(): void;

  getValueObject<T>(): T;

  getRawObject<T>(): T;

  apply: Apply;

  setValue(
    changes: any,
    isTouched?: boolean,
    triggerChange?: boolean,
    needClean?: boolean
  ): void;
}

export function MobxField(defaultValue?: any, fieldType?: FieldType) {
  return (target: any, name: string) => {
    if (!target[name]) {
      Object.defineProperty(target, name, {
        enumerable: true,
        configurable: true,
        value: new MobxFieldControl(defaultValue, fieldType)
      });
    }
  };
}

export class MobxFormClass extends BaseForm {
  useTimeZone: boolean = false;

  @action getValueObject<K>(): K {
    const prototype = Object.getPrototypeOf(this);
    return toJS(
      Object.keys(prototype)
        .filter(
          key =>
            prototype[key] instanceof MobxFieldControl &&
            !prototype[key].isSkipped
        )
        .reduce((object: any, key: string) => {
          let value = Object.getPrototypeOf(this)[key].value;
          if (
            this.useTimeZone &&
            Object.getPrototypeOf(this)[key]._fieldType === FieldType.DATE
          ) {
            value = DateTimeService.reduceTimeZone(value).toISOString();
          }
          object[key] = value instanceof Array ? value.toString() : value;
          return object;
        }, {}) as K
    );
  }

  @action getRawObject<K>(): K {
    const prototype = Object.getPrototypeOf(this);
    return toJS(
      Object.keys(prototype)
        .filter(
          key =>
            prototype[key] instanceof MobxFieldControl &&
            !prototype[key].isSkipped
        )
        .reduce((object: any, key: string) => {
          let value = Object.getPrototypeOf(this)[key].value;
          if (
            this.useTimeZone &&
            Object.getPrototypeOf(this)[key]._fieldType === FieldType.DATE
          ) {
            value = DateTimeService.reduceTimeZone(value).toISOString();
          }
          object[key] =
            value instanceof Array
              ? value
                  .slice()
                  .map(item => (item instanceof Object ? toJS(item) : item))
              : value instanceof Object
                ? toJS(value)
                : value;
          return object;
        }, {}) as K
    );
  }

  @action apply(
    changes: any,
    { disableParseDateToTimezone }: ApplySettings = {}
  ): void {
    Object.keys(changes).forEach(key => {
      const value =
        this.useTimeZone &&
        this[key]?._fieldType === FieldType.DATE &&
        changes[key] &&
        !disableParseDateToTimezone
          ? DateTimeService.parseDateAccordingTimeZone(changes[key])
          : changes[key];

      this[key]?.setValue(value, true, false);
    });
  }

  @action setValue(
    changes: any,
    isTouched: boolean = true,
    triggerChange: boolean = false,
    needClean: boolean = true
  ): void {
    if (needClean) {
      this.clear();
    }
    Object.keys(changes).forEach(key => {
      const value =
        this.useTimeZone &&
        this[key]?._fieldType === FieldType.DATE &&
        changes[key]
          ? DateTimeService.parseDateAccordingTimeZone(changes[key])
          : changes[key];
      this[key]?.setValue(value, isTouched, triggerChange);
    });
  }

  protected get _fields(): MobxFieldControl[] {
    const prototype = Object.getPrototypeOf(this);
    return Object.keys(prototype)
      .filter((key: string) => prototype[key] instanceof MobxFieldControl)
      .map(key => this[key]);
  }

  protected getFieldKeys(): string[] {
    const prototype = Object.getPrototypeOf(this);
    return Object.keys(prototype).filter(
      (key: string) => prototype[key] instanceof MobxFieldControl
    );
  }

  protected getFieldByKey(key: string): MobxFieldControl | undefined {
    return this[key] instanceof MobxFieldControl ? this[key] : undefined;
  }
}
